import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import lotDetailsResponse from "../responses/LotDetails.json";

// API URL - currently not used because we are using hardcoded response
const API_BASE = "https://api.farmfox.co/farmfox/supplier/lot";
// const API_BASE = "http://localhost:8080/farmfox/supplier/lot";

const ORANGE = "#ff4d1f";

export default function LotDetails({ lotNo: lotNoProp }) {
  const [searchParams] = useSearchParams();

  const lotNoFromUrl = searchParams.get("lotNo") || "";

  const initialLotNo = useMemo(() => {
    return (lotNoProp || lotNoFromUrl || "")
      .toUpperCase()
      .slice(0, 3);
  }, [lotNoProp, lotNoFromUrl]);

  const [chars, setChars] = useState(
    Array.from(
      { length: 3 },
      (_, i) => initialLotNo[i] || ""
    )
  );

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const inputRefs = useRef([]);

  const joinedLotNo = chars.join("").toUpperCase();

  /**
   * Handle individual lot number character input
   */
  const handleChange = (value, index) => {
    const val = value.toUpperCase().slice(0, 1);

    const next = [...chars];
    next[index] = val;

    setChars(next);

    if (val && index < 2) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  /**
   * Handle backspace navigation
   */
  const handleKeyDown = (event, index) => {
    if (
      event.key === "Backspace" &&
      !chars[index] &&
      index > 0
    ) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  /**
   * Fetch / load lot details
   *
   * Currently using hardcoded JSON response.
   */
  const fetchLotDetails = async (lotNumber) => {
    const lot = lotNumber.toUpperCase().trim();

    // Validate 3 characters
    if (lot.length !== 3) {
      setError("Enter all 3 characters");
      setData(null);
      return;
    }

    setLoading(true);
    setError("");
    setData(null);

    try {
      /*
       * IMPORTANT:
       * Create a NEW object instead of modifying
       * lotDetailsResponse directly.
       *
       * This prevents the imported JSON object from
       * being mutated between requests.
       */
      const result = {
        ...lotDetailsResponse
      };

      /*
       * Set lot number based on user input.
       *
       * Example:
       * 001 -> result.lotNo = "001"
       * 002 -> result.lotNo = "002"
       */
      if (lot === "001") {
        result.lotNo = "001";
      } else if (lot === "002") {
        result.lotNo = "002";
      } else {
        setError("Invalid Lot Number");
        setData(null);
        return;
      }

      console.log("Entered Lot:", lot);
      console.log("Lot Response:", result);
      console.log("Response Lot No:", result.lotNo);

      /*
       * Validate response
       */
      if (
        result.lotNo == null ||
        result.lotNo === "null" ||
        result.lotNo === ""
      ) {
        setError("Invalid Lot Number");
        setData(null);
        return;
      }

      /*
       * Store the NEW object in React state.
       */
      setData(result);

    } catch (err) {
      console.error("Lot verification error:", err);
      setError("Invalid Lot Number");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Automatically load lot details if
   * a 3-character lot number is provided in URL/props.
   */
  useEffect(() => {
    if (initialLotNo.length === 3) {
      fetchLotDetails(initialLotNo);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Brand */}
        <h2 style={styles.brand}>
          farmfox.co
        </h2>

        {/* Title */}
        <h3 style={styles.title}>
          Code Verification
        </h3>

        {/* Subtitle */}
        <p style={styles.subtitle}>
          Enter batch number printed on the pack
        </p>

        {/* Lot Number Inputs */}
        <div style={styles.inputRow}>
          {chars.map((char, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              id={`box-${index}`}
              maxLength="1"
              value={char}
              style={styles.box}
              onChange={(e) =>
                handleChange(e.target.value, index)
              }
              onKeyDown={(e) =>
                handleKeyDown(e, index)
              }
              aria-label={`Lot character ${index + 1}`}
            />
          ))}
        </div>

        {/* Verify Button */}
        <button
          style={styles.button}
          onClick={() =>
            fetchLotDetails(joinedLotNo)
          }
          disabled={loading}
        >
          {loading
            ? "PROCESSING..."
            : "VERIFY AND PROCESS"}
        </button>

        {/* Loading Status */}
        {loading && (
          <p style={styles.status}>
            Processing...
          </p>
        )}

        {/* Error */}
        {error && (
          <p style={styles.error}>
            {error}
          </p>
        )}

        {/* Lot Details */}
        {data && (
          <div style={styles.card}>

            <p style={styles.cardLabel}>
              MARKETED BY:
            </p>

            <p style={styles.cardValue}>
              <b>{data.companyName}</b>
            </p>

            <p style={styles.cardText}>
              {data.companyAddress}
            </p>

            <p style={styles.cardText}>
              <b>Batch No: </b>
              {data.lotNo}
            </p>

            <p style={styles.cardText}>
              <b>Mfg Date: </b>
              {data.mfgDate}
            </p>

            <p style={styles.cardText}>
              <b>Manufactured By: </b>
              {data.manufacturedBy}
            </p>

            <p style={styles.cardText}>
              <b>Manufacturer Address: </b>
              {data.manufacturerAddress}
            </p>

            <p style={styles.cardText}>
              <b>FSSAI License No: </b>
              {data.fssaiLicenseNo}
            </p>

          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Styles
 */
const styles = {
  page: {
    fontFamily: "Arial, sans-serif",
    backgroundColor: "#f4f6f8",
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
  },

  container: {
    width: "100%",
    maxWidth: "420px",
    padding: "25px",
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
    textAlign: "center",
  },

  brand: {
    color: ORANGE,
    fontWeight: "bold",
    marginBottom: "10px",
  },

  title: {
    margin: "0 0 8px",
  },

  subtitle: {
    margin: "0 0 16px",
    color: "#4b5563",
    lineHeight: 1.5,
  },

  inputRow: {
    display: "flex",
    justifyContent: "center",
    gap: "8px",
    marginTop: "15px",
    flexWrap: "wrap",
  },

  box: {
    width: "42px",
    height: "42px",
    fontSize: "18px",
    textAlign: "center",
    borderRadius: "6px",
    border: `2px solid ${ORANGE}`,
    outline: "none",
    textTransform: "uppercase",
  },

  button: {
    marginTop: "20px",
    width: "100%",
    padding: "12px",
    backgroundColor: ORANGE,
    color: "white",
    border: "none",
    fontSize: "15px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 700,
  },

  status: {
    marginTop: "10px",
    color: "#374151",
  },

  card: {
    marginTop: "20px",
    padding: "15px",
    border: `2px solid ${ORANGE}`,
    borderRadius: "10px",
    textAlign: "left",
    background: "#fff",
  },

  cardLabel: {
    margin: "0 0 8px",
    fontWeight: 700,
  },

  cardValue: {
    margin: "0 0 8px",
  },

  cardText: {
    margin: "0 0 8px",
    wordBreak: "break-word",
  },

  error: {
    color: "red",
    marginTop: "10px",
    fontWeight: 600,
  },
}